<script setup lang="ts">
import { computed, provide, watch, type Ref } from 'vue'
import { useRouter } from 'vue-router'
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/vue/24/outline'
import BackPageLayout from './BackPageLayout.vue'
import { useSalesReportsStore } from '../stores/salesReports'
import { useUserManagerStore } from '../stores/userManagerStore'
import { usePeriodShift } from '../composables/usePeriodShift'
import { getCenterColorById } from '../utils/CenterColors'
import type { SalesReport } from '../models/SalesReport'

defineOptions({ inheritAttrs: false })

defineProps<{
  backPage?: import('vue-router').RouteLocationNamedRaw
}>()

const emit = defineEmits<{
  'period-changed': []
}>()

const router = useRouter()
const salesReportsStore = useSalesReportsStore()
const userManagerStore = useUserManagerStore()

const centerId = parseInt(router.currentRoute.value.params.centerId as string)
const srId = router.currentRoute.value.params.id as string

const card = computed(() => {
  return salesReportsStore.getReportForCenterAndComponent(centerId, srId)
})

const { periodOffset, periodTransition, periodLabel, shiftPeriod, resetPeriod, applyShift } =
  usePeriodShift(card as Ref<SalesReport | undefined>)

watch(periodOffset, () => {
  if (!card.value) return
  if (card.value.type === 'custom') return
  applyShift()
  emit('period-changed')
})

const isMultiCenter = computed(() => userManagerStore.displayedCenters.length > 1)
const centerName = computed(() => {
  return userManagerStore.user?.centers.find(c => c.id === centerId)?.title
})
const centerColor = computed(() => {
  return getCenterColorById(centerId, userManagerStore.displayedCenters)
})

provide('reportCard', card)
provide('reportCenterId', centerId)
provide('reportSrId', srId)
</script>

<template>
  <BackPageLayout v-bind="$attrs" :back-page="backPage">
    <template #headline>
      <slot name="headline">
        <div class="flex flex-col">
          <span>{{ card?.title ?? '' }}</span>
          <span v-if="isMultiCenter && centerName" class="flex items-center gap-1.5 text-xs font-normal text-muted-foreground">
            <span class="w-1.5 h-1.5 rounded-full shrink-0" :style="{ backgroundColor: centerColor }"></span>
            {{ centerName }}
          </span>
        </div>
      </slot>
    </template>

    <!-- Period navigation -->
    <div v-if="card && card.type !== 'custom'" class="mb-4">
      <div class="cr-period-card rounded-2xl relative overflow-hidden">
        <div class="px-4 pt-3.5 pb-3">
          <div class="flex items-center justify-between">
            <button
              @click="shiftPeriod(-1)"
              class="cr-arrow-btn w-9 h-9 rounded-xl flex items-center justify-center active:scale-90 transition-all duration-150"
            >
              <ChevronLeftIcon class="h-4 w-4 stroke-[2.5]" />
            </button>

            <div class="flex-1 mx-3 text-center relative overflow-hidden h-6">
              <Transition :name="periodTransition" mode="out-in">
                <span
                  :key="periodLabel"
                  class="text-[15px] font-bold tracking-tight text-foreground absolute inset-0 flex items-center justify-center"
                >
                  {{ periodLabel }}
                </span>
              </Transition>
            </div>

            <button
              @click="shiftPeriod(1)"
              :disabled="periodOffset >= 0"
              class="cr-arrow-btn w-9 h-9 rounded-xl flex items-center justify-center active:scale-90 transition-all duration-150 disabled:opacity-25"
            >
              <ChevronRightIcon class="h-4 w-4 stroke-[2.5]" />
            </button>
          </div>

          <Transition name="cr-offset-reveal">
            <div v-if="periodOffset !== 0" class="flex items-center justify-center mt-2.5">
              <button
                @click="resetPeriod"
                class="cr-reset-pill text-[11px] font-semibold px-2.5 py-0.5 rounded-full active:scale-95 transition-all"
              >
                {{ $t('editcomponent.sheet.reset_period') }}
              </button>
            </div>
          </Transition>
        </div>
      </div>
    </div>

    <slot />

    <template v-if="$slots.footer" #footer>
      <slot name="footer" />
    </template>
  </BackPageLayout>
</template>

<style scoped>
/* Period card */
.cr-period-card {
  background: var(--card);
  border: 1px solid rgba(0, 0, 0, 0.06);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04), 0 4px 14px rgba(0, 0, 0, 0.03);
}
.cr-arrow-btn {
  color: var(--foreground);
  background: rgba(0, 0, 0, 0.04);
  border: 1px solid rgba(0, 0, 0, 0.05);
}
.cr-arrow-btn:active:not(:disabled) {
  background: rgba(6, 200, 255, 0.10);
  border-color: rgba(6, 200, 255, 0.20);
}
.cr-reset-pill {
  color: #06C8FF;
  background: rgba(6, 200, 255, 0.08);
  border: 1px solid rgba(6, 200, 255, 0.15);
}
.cr-reset-pill:active {
  background: rgba(6, 200, 255, 0.15);
}

/* Dark mode */
:is(.dark *).cr-period-card {
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.07);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.15), 0 4px 14px rgba(0, 0, 0, 0.10);
}
:is(.dark *).cr-arrow-btn {
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.06);
}
:is(.dark *).cr-arrow-btn:active:not(:disabled) {
  background: rgba(6, 200, 255, 0.12);
  border-color: rgba(6, 200, 255, 0.22);
}
:is(.dark *).cr-reset-pill {
  background: rgba(6, 200, 255, 0.10);
  border: 1px solid rgba(6, 200, 255, 0.18);
}

/* Period label slide transitions */
.cr-label-slide-left-enter-active,
.cr-label-slide-left-leave-active,
.cr-label-slide-right-enter-active,
.cr-label-slide-right-leave-active {
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
}
.cr-label-slide-left-enter-from { opacity: 0; transform: translateX(16px); }
.cr-label-slide-left-leave-to { opacity: 0; transform: translateX(-16px); }
.cr-label-slide-right-enter-from { opacity: 0; transform: translateX(-16px); }
.cr-label-slide-right-leave-to { opacity: 0; transform: translateX(16px); }

/* Reset pill reveal */
.cr-offset-reveal-enter-active { transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1); }
.cr-offset-reveal-leave-active { transition: all 0.2s ease; }
.cr-offset-reveal-enter-from { opacity: 0; max-height: 0; margin-top: 0; }
.cr-offset-reveal-enter-to { opacity: 1; max-height: 32px; margin-top: 10px; }
.cr-offset-reveal-leave-from { opacity: 1; max-height: 32px; margin-top: 10px; }
.cr-offset-reveal-leave-to { opacity: 0; max-height: 0; margin-top: 0; }
</style>
